import React, { useState, useEffect } from 'react'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { Select } from 'components/ui/Select'
import { Badge } from 'components/ui/Badge'
import { teamsApi, candidatesApi, type Candidate, type Team } from 'lib/api'
import { Save, X, Users, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { useToast } from 'contexts/ToastContext'
import { useTranslation } from 'react-i18next'

interface EditCandidateModalProps {
  isOpen: boolean
  onClose: () => void
  candidate: Candidate | null
  onCandidateUpdated: (candidate: Candidate) => void
}

export function EditCandidateModal({ isOpen, onClose, candidate, onCandidateUpdated }: EditCandidateModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    experience: '',
    location: '',
    bio: '',
    skills: [] as string[],
    soft_skills: [] as string[],
    main_missions: [] as Array<string | { description?: string; nom?: string }>,
    certifications: [] as Array<string | { nom?: string }>,
    languages: [] as Array<{ name: string; level?: string | null }>,
    education: [] as any[],
    projets: [] as any[],
    team: '',
    linkedin_url: '',
    github_url: '',
    twitter_url: '',
    portfolio_url: ''
  })
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(false)
  const [newSkill, setNewSkill] = useState('')
  const [newSoftSkill, setNewSoftSkill] = useState('')
  const [newMission, setNewMission] = useState('')
  const [newCertification, setNewCertification] = useState('')
  const [showCertifications, setShowCertifications] = useState(false)
  const [showLanguages, setShowLanguages] = useState(false)
  const [showEducation, setShowEducation] = useState(false)
  const [showProjects, setShowProjects] = useState(false)

  // Populate form when candidate changes
  useEffect(() => {
    if (candidate && isOpen) {
      // Extract all fields from candidate data
      const softSkills = (candidate as any).soft_skills || []
      const mainMissions = (candidate as any).main_missions || []
      const certifications = (candidate as any).certifications || []
      const languages = (candidate as any).languages || []
      const education = (candidate as any).education || []
      const projets = (candidate as any).projets || []
      
      setFormData({
        name: candidate.name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        role: candidate.role || '',
        experience: candidate.experience || '',
        location: candidate.location || '',
        bio: candidate.bio || '',
        skills: candidate.skills || [],
        soft_skills: Array.isArray(softSkills) ? softSkills : [],
        main_missions: Array.isArray(mainMissions) ? mainMissions : [],
        certifications: Array.isArray(certifications) ? certifications : [],
        languages: Array.isArray(languages) ? languages : [],
        education: Array.isArray(education) ? education : [],
        projets: Array.isArray(projets) ? projets : [],
        team: '', // We'll need to find which team the candidate belongs to
        linkedin_url: candidate.linkedin_url || '',
        github_url: candidate.github_url || '',
        twitter_url: candidate.twitter_url || '',
        portfolio_url: candidate.portfolio_url || ''
      })
    }
  }, [candidate, isOpen])

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await teamsApi.getAll()
        setTeams(response.data.results || [])
      } catch (error) {
        console.error('Error fetching teams:', error)
      }
    }
    if (isOpen) {
      fetchTeams()
    }
  }, [isOpen])


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!candidate) return

    setLoading(true)
    try {
      // Prepare update data
      const updateData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        experience: formData.experience,
        location: formData.location,
        bio: formData.bio,
        skills: formData.skills,
        linkedin_url: formData.linkedin_url,
        github_url: formData.github_url,
        twitter_url: formData.twitter_url,
        portfolio_url: formData.portfolio_url
      }
      
      // Include all extracted CV data if backend supports them
      if (formData.soft_skills.length > 0) {
        updateData.soft_skills = formData.soft_skills
      }
      if (formData.main_missions.length > 0) {
        updateData.main_missions = formData.main_missions
      }
      if (formData.certifications.length > 0) {
        updateData.certifications = formData.certifications
      }
      if (formData.languages.length > 0) {
        updateData.languages = formData.languages
      }
      if (formData.education.length > 0) {
        updateData.education = formData.education
      }
      if (formData.projets.length > 0) {
        updateData.projets = formData.projets
      }

      // Update candidate
      const response = await candidatesApi.update(candidate.id, updateData)
      
      addToast({
        type: 'success',
        title: t('candidateDetail.candidateUpdated'),
        message: t('candidateDetail.candidateUpdatedSuccess'),
        duration: 5000
      })

      onCandidateUpdated(response.data)
      handleClose()
    } catch (error: any) {
      console.error('Error updating candidate:', error)
      const errorMessage = error.response?.data?.error || error.message || t('candidateDetail.updateError')
      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorMessage,
        duration: 7000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
      experience: '',
      location: '',
      bio: '',
      skills: [],
      soft_skills: [],
      main_missions: [],
      certifications: [],
      languages: [],
      education: [],
      projets: [],
      team: '',
      linkedin_url: '',
      github_url: '',
      twitter_url: '',
      portfolio_url: ''
    })
    setNewSkill('')
    setNewSoftSkill('')
    setNewMission('')
    setNewCertification('')
    setShowCertifications(false)
    setShowLanguages(false)
    setShowEducation(false)
    setShowProjects(false)
    onClose()
  }

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }))
      setNewSkill('')
    }
  }

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }))
  }

  const addSoftSkill = () => {
    if (newSoftSkill.trim() && !formData.soft_skills.includes(newSoftSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        soft_skills: [...prev.soft_skills, newSoftSkill.trim()]
      }))
      setNewSoftSkill('')
    }
  }

  const removeSoftSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      soft_skills: prev.soft_skills.filter(skill => skill !== skillToRemove)
    }))
  }

  const addMission = () => {
    if (newMission.trim() && !formData.main_missions.includes(newMission.trim())) {
      setFormData(prev => ({
        ...prev,
        main_missions: [...prev.main_missions, newMission.trim()]
      }))
      setNewMission('')
    }
  }

  const removeMission = (missionToRemove: string | { description?: string; nom?: string }) => {
    setFormData(prev => ({
      ...prev,
      main_missions: prev.main_missions.filter(mission => {
        if (typeof mission === 'string' && typeof missionToRemove === 'string') {
          return mission !== missionToRemove
        }
        if (typeof mission === 'object' && typeof missionToRemove === 'object') {
          return mission !== missionToRemove
        }
        return true
      })
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addSkill()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('candidateDetail.editCandidate')}>
      <div className="space-y-6 max-w-2xl p-1">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidateDetail.basicInformation')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidates.name')} *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('candidates.namePlaceholder')}
                  disabled={loading}
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidates.email')} *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={t('candidates.emailPlaceholder')}
                  disabled={loading}
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidates.phone')}</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={t('candidates.phonePlaceholder')}
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidates.role')} *</label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  placeholder={t('candidates.rolePlaceholder')}
                  disabled={loading}
                  required
                />
              </div>
            </div>
          </div>

          {/* Status and Experience */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidateDetail.statusAndExperience')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidates.experience')}</label>
                <Input
                  value={formData.experience}
                  onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                  placeholder={t('candidates.experiencePlaceholder')}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Location and Bio */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidateDetail.additionalInformation')}</h3>
            
            <div>
              <label className="text-sm font-medium text-foreground">{t('candidates.location')}</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder={t('candidates.locationPlaceholder')}
                disabled={loading}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground">{t('candidates.bio')}</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder={t('candidates.bioPlaceholder')}
                disabled={loading}
                rows={3}
              />
            </div>
          </div>

          {/* Social Media Links */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidateDetail.socialMedia')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidateDetail.linkedinUrl')}</label>
                <Input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  placeholder="https://linkedin.com/in/username"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidateDetail.githubUrl')}</label>
                <Input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, github_url: e.target.value }))}
                  placeholder="https://github.com/username"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidateDetail.twitterUrl')}</label>
                <Input
                  type="url"
                  value={formData.twitter_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, twitter_url: e.target.value }))}
                  placeholder="https://twitter.com/username"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">{t('candidateDetail.portfolioUrl')}</label>
                <Input
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, portfolio_url: e.target.value }))}
                  placeholder="https://portfolio-website.com"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Technical Skills */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidates.skills', 'Technical Skills')}</h3>
            
            <div className="flex space-x-2">
              <Input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder={t('candidateDetail.addSkillPlaceholder', 'Add a skill...')}
                disabled={loading}
                onKeyPress={handleKeyPress}
              />
              <Button
                type="button"
                onClick={addSkill}
                disabled={loading || !newSkill.trim()}
                size="sm"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {formData.skills.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center space-x-1">
                    <span>{skill}</span>
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-1 hover:text-red-500"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Soft Skills */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidates.softSkills', 'Soft Skills')}</h3>
            
            <div className="flex space-x-2">
              <Input
                value={newSoftSkill}
                onChange={(e) => setNewSoftSkill(e.target.value)}
                placeholder={t('candidates.addSoftSkill', 'Add a soft skill (e.g., Communication, Leadership)')}
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addSoftSkill()
                  }
                }}
              />
              <Button
                type="button"
                onClick={addSoftSkill}
                disabled={loading || !newSoftSkill.trim()}
                size="sm"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {formData.soft_skills.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.soft_skills.map((skill, index) => (
                  <Badge key={index} variant="outline" className="flex items-center space-x-1">
                    <span>{skill}</span>
                    <button
                      type="button"
                      onClick={() => removeSoftSkill(skill)}
                      className="ml-1 hover:text-red-500"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Main Missions */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-foreground">{t('candidates.mainMissions', 'Main Missions / Responsibilities')}</h3>
            
            <div className="flex space-x-2">
              <Input
                value={newMission}
                onChange={(e) => setNewMission(e.target.value)}
                placeholder={t('candidates.addMission', 'Add a mission or responsibility...')}
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addMission()
                  }
                }}
              />
              <Button
                type="button"
                onClick={addMission}
                disabled={loading || !newMission.trim()}
                size="sm"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {formData.main_missions.length > 0 && (
              <div className="space-y-2">
                {formData.main_missions.map((mission, index) => (
                  <div key={index} className="flex items-start gap-2 p-2 bg-muted rounded-lg">
                    <span className="flex-1 text-sm">{typeof mission === 'string' ? mission : mission.description || mission.nom || ''}</span>
                    <button
                      type="button"
                      onClick={() => removeMission(mission)}
                      className="text-muted-foreground hover:text-red-500"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Certifications */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">
                {t('candidates.certifications', 'Certifications')}
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowCertifications(!showCertifications)}
              >
                {showCertifications ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
            {showCertifications && (
              <>
                <div className="flex space-x-2">
                  <Input
                    value={newCertification}
                    onChange={(e) => setNewCertification(e.target.value)}
                    placeholder={t('candidates.addCertification', 'Add certification...')}
                    disabled={loading}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (newCertification.trim()) {
                          setFormData({ ...formData, certifications: [...formData.certifications, newCertification.trim()] })
                          setNewCertification('')
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (newCertification.trim()) {
                        setFormData({ ...formData, certifications: [...formData.certifications, newCertification.trim()] })
                        setNewCertification('')
                      }
                    }}
                    disabled={loading || !newCertification.trim()}
                    size="sm"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-1 px-2 py-1 bg-muted rounded-full text-sm">
                        <span>{typeof cert === 'string' ? cert : cert.nom || ''}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, certifications: formData.certifications.filter((_, i) => i !== index) })
                          }}
                          className="text-muted-foreground hover:text-red-500"
                          disabled={loading}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Languages */}
          {formData.languages.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('candidates.languages', 'Languages')}
              </label>
              <div className="flex flex-wrap gap-2">
                {formData.languages.map((lang, index) => (
                  <div key={index} className="px-2 py-1 bg-muted rounded-full text-sm">
                    {typeof lang === 'string' ? lang : `${lang.name || ''}${lang.level ? ` (${lang.level})` : ''}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {formData.education.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('candidates.education', 'Education')}
              </label>
              <div className="space-y-2">
                {formData.education.map((edu, index) => (
                  <div key={index} className="p-2 bg-muted rounded-lg text-sm">
                    {typeof edu === 'string' ? edu : `${edu.diplome || ''}${edu.etablissement ? ` - ${edu.etablissement}` : ''}${edu.annee ? ` (${edu.annee})` : ''}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects */}
          {formData.projets.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('candidates.projects', 'Projects')}
              </label>
              <div className="space-y-2">
                {formData.projets.map((proj, index) => (
                  <div key={index} className="p-3 bg-muted rounded-lg text-sm space-y-1">
                    <div className="font-medium">
                      {typeof proj === 'string' ? proj : proj.nom || proj.name || proj.title || ''}
                    </div>
                    {typeof proj === 'object' && proj.description && (
                      <div className="text-muted-foreground">{proj.description}</div>
                    )}
                    {typeof proj === 'object' && proj.technologies && proj.technologies.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {proj.technologies.map((tech: string, techIndex: number) => (
                          <span key={techIndex} className="px-2 py-0.5 bg-background rounded text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('common.save')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
